import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { UtmSources } from '../models/Statistics';

interface Props {
    data: UtmSources | null;
    period: 'last7days' | 'last30days';
}

const COLORS = ['#1A3B40', '#2E6A73', '#4399A6', '#66EAFD', '#B2D6DA', '#6D979D'];

const UtmSourceChart: React.FC<Props> = ({ data, period }) => {
    const { t } = useTranslation();

    const raw = (data?.[period] ?? [])
        .slice()
        .sort((a, b) => b.count - a.count)
        .slice(0, 8)
        .map(item => ({
            ...item,
            source: item.source || t('statistics.utm_unknown'),
        }));

    if (raw.length === 0) {
        return (
            <div className="flex items-center justify-center h-[180px] text-utility-mutedText text-sm">
                {t('statistics.no_data')}
            </div>
        );
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload?.length) return null;
        const { source, count } = payload[0].payload;
        return (
            <div className="bg-white border border-neutral rounded p-2 text-sm shadow">
                <span className="font-semibold text-primary-dark">{source}</span>
                <span className="ml-2 text-utility-mutedText">{count.toLocaleString()} {t('statistics.visitors')}</span>
            </div>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={220}>
            <BarChart
                layout="vertical"
                data={raw}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
            >
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                    type="category"
                    dataKey="source"
                    tick={{ fontSize: 12 }}
                    width={90}
                    tickFormatter={(v: string) => v.length > 12 ? v.slice(0, 11) + '…' : v}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f0f9fa' }} />
                <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                    {raw.map((_, i) => (
                        <Cell key={`utm-cell-${i}`} fill={COLORS[i % COLORS.length]} fillOpacity={0.85} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default UtmSourceChart;
